import { Mic, MicOff, Radio as RadioIcon, Volume2, WifiOff } from 'lucide-react';
import { useEffect, useId, useRef, useState, useSyncExternalStore, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import type { RadioSpeaker } from '@despachador/domain';
import { pttErrorText } from '@despachador/contracts/ptt-errors';
import type { RadioClient, RadioSnapshot } from '@despachador/api-client/ptt';

/** Subscribes a component to the radio client (the single source of truth). */
export function useRadio(client: RadioClient | null): RadioSnapshot | null {
  return useSyncExternalStore(
    client ? client.subscribe : noopSubscribe,
    client ? client.getSnapshot : nullSnapshot,
    client ? client.getSnapshot : nullSnapshot,
  );
}
const noopSubscribe = () => () => undefined;
const nullSnapshot = () => null;

/** Seconds since `since` (epoch ms), ticking every 250 ms while mounted. */
export function useElapsed(since: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (since === null) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [since]);
  return since === null ? 0 : Math.max(0, Math.floor((now - since) / 1000));
}

export const clock = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

export type PttVisual = 'offline' | 'connecting' | 'mic-blocked' | 'listen-only' | 'no-channel' | 'busy' | 'ready' | 'requesting' | 'talking' | 'releasing';

/** One place deciding what the button shows. Never color alone: every state has text + icon. */
export function pttVisual(s: RadioSnapshot | null): { visual: PttVisual; label: string; hint: string } {
  if (!s) return { visual: 'offline', label: 'Radio', hint: 'Radio apagada' };
  const p = s.link.phase;
  if (p === 'ERROR') return { visual: 'offline', label: 'Radio no disponible', hint: pttErrorText(s.link.error) };
  if (p === 'DISCONNECTED') return { visual: 'offline', label: 'Sin conexión', hint: 'La radio está desconectada' };
  if (p === 'CONNECTING') return { visual: 'connecting', label: 'Conectando…', hint: 'Conectando la radio' };
  if (p === 'RECONNECTING') return { visual: 'connecting', label: 'Reconectando…', hint: 'Se perdió la conexión; reconectando' };
  if (p === 'REQUESTING') return { visual: 'requesting', label: 'Espera…', hint: 'Pidiendo el turno' };
  if (p === 'TRANSMITTING') return { visual: 'talking', label: 'Hablando', hint: 'Estás al aire. Suelta para terminar' };
  if (p === 'RELEASING') return { visual: 'releasing', label: 'Soltando…', hint: 'Terminando la transmisión' };
  const ch = s.activeChannelId ? s.channels[s.activeChannelId] : null;
  if (!ch) return { visual: 'no-channel', label: 'Sin canal', hint: 'Elige un canal' };
  if (s.mic === 'denied') return { visual: 'mic-blocked', label: 'Micrófono bloqueado', hint: pttErrorText('MICROPHONE_PERMISSION_DENIED') };
  if (s.mic === 'unavailable') return { visual: 'mic-blocked', label: 'Sin micrófono', hint: pttErrorText('MICROPHONE_UNAVAILABLE') };
  if (!ch.canTransmit) return { visual: 'listen-only', label: 'Solo escucha', hint: pttErrorText('TRANSMISSION_DENIED') };
  if (ch.speaker && ch.speaker.userId !== s.userId) return { visual: 'busy', label: 'Canal ocupado', hint: `${ch.speaker.name} está hablando` };
  if (!s.mediaReady) return { visual: 'connecting', label: 'Audio…', hint: 'Preparando el audio' };
  return { visual: 'ready', label: 'Hablar', hint: 'Mantén presionado para hablar' };
}

const DISABLED: ReadonlySet<PttVisual> = new Set(['offline', 'connecting', 'mic-blocked', 'listen-only', 'no-channel']);

/**
 * Push-to-talk button. Hold to talk with mouse, touch, pen, Space or Enter. Any way the press can
 * end — pointer up/cancel, lost capture, blur, unmount — releases. The radio client additionally
 * releases on hidden page / background, and the server caps every turn: a stuck mic is impossible.
 */
export function PttButton({ client, snapshot, size = 'driver', hotkey = false }: { client: RadioClient; snapshot: RadioSnapshot; size?: 'driver' | 'console' | 'dock'; hotkey?: boolean }) {
  const hintId = useId();
  const { visual, label, hint } = pttVisual(snapshot);
  const talking = visual === 'talking';
  const startedAt = snapshot.link.phase === 'TRANSMITTING' ? snapshot.link.startedAt : null;
  const maxMs = snapshot.link.phase === 'TRANSMITTING' ? snapshot.link.maxUntil - snapshot.link.startedAt : 0;
  const elapsed = useElapsed(startedAt);
  const btn = useRef<HTMLButtonElement>(null);
  const disabled = DISABLED.has(visual);

  useEffect(() => () => client.release(), [client]);

  // console: hold Space anywhere (outside form fields) to talk
  useEffect(() => {
    if (!hotkey) return;
    const typing = (e: globalThis.KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      return !!t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName));
    };
    const down = (e: globalThis.KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat || typing(e)) return;
      e.preventDefault();
      client.press();
    };
    const up = (e: globalThis.KeyboardEvent) => {
      if (e.code !== 'Space' || typing(e)) return;
      e.preventDefault();
      client.release();
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [client, hotkey]);

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // capture unsupported: pointerup on the button still works
    }
    client.press();
  };
  const end = () => client.release();
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
      e.preventDefault();
      e.stopPropagation();
      client.press();
    }
  };
  const onKeyUp = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      client.release();
    }
  };
  const Icon = visual === 'offline' || visual === 'connecting' ? WifiOff : visual === 'mic-blocked' || visual === 'listen-only' ? MicOff : Mic;
  const pct = talking && maxMs > 0 ? Math.min(100, (elapsed * 1000 * 100) / maxMs) : 0;

  return (
    <div className={`ptt ptt-${size}`}>
      <button
        ref={btn}
        type="button"
        className={`ptt-btn ptt-${visual}`}
        aria-pressed={talking}
        aria-disabled={disabled}
        aria-describedby={hintId}
        disabled={disabled && visual !== 'mic-blocked'}
        onPointerDown={disabled ? undefined : onPointerDown}
        onPointerUp={end}
        onPointerCancel={end}
        onLostPointerCapture={end}
        onPointerLeave={(e) => e.pointerType === 'mouse' && e.buttons === 0 && end()}
        onKeyDown={disabled ? undefined : onKeyDown}
        onKeyUp={onKeyUp}
        onBlur={end}
        onContextMenu={(e) => e.preventDefault()}
        style={talking ? ({ '--ptt-progress': `${pct}%` } as CSSProperties) : undefined}
      >
        <Icon size={size === 'driver' ? 56 : size === 'dock' ? 24 : 32} aria-hidden />
        <span className="ptt-label">{label}</span>
        {talking && <span className="ptt-timer">{clock(elapsed)}</span>}
      </button>
      <div id={hintId} className={size === 'dock' ? 'sr-only' : 'ptt-hint'} aria-live="polite">
        {hint}
      </div>
    </div>
  );
}

/** "🔴 Carlos Pérez (M-12) está hablando 00:07" — who holds the channel, readable at a glance. */
export function SpeakerBanner({ speaker, channelName, self, compact }: { speaker: RadioSpeaker | null; channelName?: string; self?: boolean; compact?: boolean }) {
  const elapsed = useElapsed(speaker?.startedAt ?? null);
  if (!speaker)
    return (
      <div className={`ptt-speaker idle${compact ? ' compact' : ''}`} aria-live="polite">
        <RadioIcon size={18} aria-hidden /> <span>Canal libre{channelName ? ` · ${channelName}` : ''}</span>
      </div>
    );
  return (
    <div className={`ptt-speaker live${compact ? ' compact' : ''}`} role="status" aria-live="polite">
      <span className="ptt-avatar" aria-hidden>
        {initials(speaker.name)}
      </span>
      <span className="ptt-speaker-text">
        <b>
          <span className="ptt-dot" aria-hidden />
          {self ? 'Tú' : speaker.name}
          {speaker.driverCode ? <span className="muted"> · {speaker.driverCode}</span> : null}
        </b>
        <span>
          {self ? 'Estás transmitiendo' : 'Transmitiendo…'} <span className="ptt-timer">{clock(elapsed)}</span>
          {channelName ? <span className="muted"> · {channelName}</span> : null}
        </span>
      </span>
      <Volume2 size={18} aria-hidden />
    </div>
  );
}

/** Connection line for the radio screens ("Conectado", "Reconectando…"), text first. */
export function radioLinkText(s: RadioSnapshot | null): { text: string; tone: 'ok' | 'warn' | 'danger' } {
  if (!s) return { text: 'Apagada', tone: 'warn' };
  switch (s.link.phase) {
    case 'READY':
    case 'REQUESTING':
    case 'TRANSMITTING':
    case 'RELEASING':
      if (!s.mediaReady) return { text: 'Conectado · audio no disponible', tone: 'warn' };
      if (s.quality?.lossPct !== null && s.quality && (s.quality.lossPct ?? 0) >= 8) return { text: 'Conectado · señal débil', tone: 'warn' };
      return { text: s.quality?.relay ? 'Conectado (relé)' : 'Conectado', tone: 'ok' };
    case 'CONNECTING':
      return { text: 'Conectando…', tone: 'warn' };
    case 'RECONNECTING':
      return { text: 'Reconectando…', tone: 'warn' };
    case 'ERROR':
      return { text: pttErrorText(s.link.error), tone: 'danger' };
    default:
      return { text: 'Sin conexión', tone: 'danger' };
  }
}

export { pttErrorText };
